import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, importProvidersFrom, inject } from '@angular/core';
import { provideRouter, withComponentInputBinding, Router, UrlSerializer } from '@angular/router';
import { LiteralParenthesesUrlSerializer } from './shared/literal-parentheses-url-serializer';
import { routes } from './app.routes';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { provideApollo } from 'apollo-angular';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';
import { split } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { AuthWaitLink } from './shared/auth/auth-wait-link';
import { createBearerTokenLink } from './shared/auth/bearer-token-link';
import { provideAuth0, AuthService, AuthClientConfig } from '@auth0/auth0-angular';
import { UserIdleModule } from 'angular-user-idle';
import { AuthGuard } from './shared/auth/auth-guard';
import { AuthTokenInterceptor } from './shared/interceptors/auth-token.interceptor';
import { AuthInitializerService } from './shared/auth/auth-initializer.service';
import { ConfigService } from './core/services/config.service';
import { WebSocketService } from './core/services/websocket.service';
import { createErrorHandler, captureException, captureMessage } from '@sentry/angular';
import { isExpectedPromptResponseStoryDeleteError } from './shared/graphql/expected-story-delete-errors';
import {
  isAuthenticatedHasuraSchemaSyncError,
  isTransientHasuraSchemaSyncError
} from './shared/graphql/transient-hasura-errors';
import { isAbortedHttpRequestError } from './shared/http/aborted-http-errors';
import { isUploadNetworkError } from './shared/http/upload-network-error';
import { isInsecureHistoryError } from './shared/browser/insecure-history';
import { HasuraSchemaSyncLink } from './shared/graphql/hasura-schema-sync-link';
import { AlertModule } from 'ngx-bootstrap/alert';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { RatingModule } from 'ngx-bootstrap/rating';
import { MonacoEditorModule, NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { provideMarkdown } from 'ngx-markdown';
import { SafePipe } from './shared/utils/safe.pipe';


const monacoConfig: NgxMonacoEditorConfig = {
  baseUrl: '/assets/monaco/min/vs',
  defaultOptions: {
    theme: 'vs-dark',
    scrollBeyondLastLine: false,
    automaticLayout: true
  },
  onMonacoLoad: () => {
    const monaco = (window as any).monaco;

    // Configure worker URLs to fix the "Failed to parse URL" error
    monaco.languages.register({ id: 'typescript' });
    monaco.languages.register({ id: 'html' });
    monaco.languages.register({ id: 'json' });

    // Set up worker paths explicitly
    // Configure MonacoEnvironment to use blob URLs for workers
    (window as any).MonacoEnvironment = {
      getWorker: function (_moduleId: string, label: string) {
        const workerUrlMap = {
          'typescript': '/assets/monaco/min/vs/language/typescript/tsWorker.js',
          'javascript': '/assets/monaco/min/vs/language/typescript/tsWorker.js',
          'html': '/assets/monaco/min/vs/language/html/htmlWorker.js',
          'json': '/assets/monaco/min/vs/language/json/jsonWorker.js',
          // 'editorWorkerService': '/assets/monaco/min/vs/editor/editor.worker.js',
        };

        const workerUrl = workerUrlMap[label] || workerUrlMap['html'];
        const fullWorkerUrl = `${window.location.origin}${workerUrl}`;
        const workerCode = `
          // AMD loader shim
          self.define = function(id, dependencies, factory) {
            if (typeof factory === 'function') {
              factory();
            }
          };
          self.define.amd = true;
          self.MonacoEnvironment = { baseUrl: '${window.location.origin}/assets/monaco/min/vs/' };
          try {
            importScripts('${fullWorkerUrl}');
          } catch (e) {
            console.error('Failed to load worker script:', e);
          }
        `;
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const worker = new Worker(URL.createObjectURL(blob));
        worker.onerror = (err) => {
          console.error('Worker error for label:', label, err);
        };
        return worker;
      },
    };

    // Register languages
    monaco.languages.register({ id: 'typescript' });
    monaco.languages.register({ id: 'html' });
    monaco.languages.register({ id: 'json' });

    // Unbind Monaco's built-in Command Palette (F1). We don't register any
    // custom actions/commands for it, and it's fundamentally broken under
    // zone.js: Monaco's async quick-access providers return native Promises,
    // but its `instanceof Promise` check compares against zone.js's patched
    // global Promise, so the check fails and the unresolved Promise gets
    // treated as an array, crashing with "reduce is not a function"
    // (see https://github.com/microsoft/monaco-editor/issues/4372, closed
    // won't-fix upstream since it's a zone.js incompatibility).
    monaco.editor.addKeybindingRule({
      keybinding: monaco.KeyCode.F1,
      command: null,
    });
  },
  requireConfig: {
  },
};

// Load app config during initialization
export function initializeAppFactory(configService: ConfigService): () => Promise<any> {
  return async () => {
    await configService.loadAppConfig(environment.configUrl);
  };
}

// Provide the Auth0 configuration using a factory that depends on ConfigService.
// This ensures that the Auth0 client is configured only after the app config is loaded.




export const appConfig: ApplicationConfig = {
  providers: [
    // CSP is now handled at the Nginx level
    provideRouter(routes, withComponentInputBinding()),
    { provide: UrlSerializer, useClass: LiteralParenthesesUrlSerializer },
    provideAnimations(),
    provideMarkdown(),
    provideHttpClient(withInterceptorsFromDi()),

    // Provide the Auth0 configuration using a factory that depends on ConfigService.
    // This ensures that the Auth0 client is configured only after the app config is loaded.
    {
      provide: AuthClientConfig,
      useFactory: (configService: ConfigService) => {
        const config = configService.getConfig();
        return new AuthClientConfig({
          domain: config.auth0.domain,
          clientId: config.auth0.clientId,
          authorizationParams: {
            ...config.auth0.authorizationParams,
            redirect_uri: `${window.location.protocol}//${window.location.host}/auth/callback`
          },
          leeway: 120,
          cacheLocation: 'localstorage',
          useCookiesForTransactions: true,
          skipRedirectCallback: window.location.pathname === '/logout' || window.location.pathname === '/auth/callback',
          useRefreshTokens: true,
          useRefreshTokensFallback: true,
          nowProvider: () => Date.now(),
          httpInterceptor: {
            allowedList: [
              {
                uri: '/api/*',
                tokenOptions: {
                  authorizationParams: {
                    audience: config.auth0.authorizationParams.audience
                  }
                }
              },
              {
                uri: '/ai/*',
                tokenOptions: {
                  authorizationParams: {
                    audience: config.auth0.authorizationParams.audience
                  }
                }
              }
            ]
          }
        });
      },
      deps: [ConfigService]
    },

    // provideAuth0 will automatically use the AuthClientConfig provider above.
    provideAuth0(),

    // Single, chained application initializer
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [ConfigService],
      multi: true
    },

    // Apollo GraphQL configuration
    provideApollo(() => {
      const httpClient = inject(HttpClient);
      const configService = inject(ConfigService);
      const httpLink = new HttpLink(httpClient).create({
        uri: configService.api.gqlUrl,
      });

      // Retry link for transient network errors (5xx, network failures)
      const retryLink = new RetryLink({
        delay: {
          initial: 300,
          max: 3000,
          jitter: true
        },
        attempts: {
          max: 3,
          retryIf: (error, operation) => {
            // Schema-sync *_root misses are retried by HasuraSchemaSyncLink with a
            // longer delay; don't nest RetryLink's 300ms retries on top of that.
            if (isAuthenticatedHasuraSchemaSyncError(error, operation.getContext())) {
              return false;
            }
            const statusCode = error?.statusCode || error?.status;
            // Retry on 5xx server errors OR status 0/undefined (network failure/timeout/abort)
            return (statusCode >= 500 && statusCode < 600) || statusCode === 0 || statusCode === undefined;
          }
        }
      });

      // Error link to log GraphQL/network errors; GraphQL errors and 5xx network errors
      // are also reported to Sentry (4xx network errors are logged only, see below)
      const errorLink = onError(({ networkError, graphQLErrors, operation }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach(({ message, locations, path, extensions }) => {
            console.error(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`);
            // Transient Hasura/Postgres blips on subscriptions (SLAPFIVE-FRONTEND-A2S/A2T)
            const isTransientPg = extensions?.['code'] === 'postgres-error'
              && /connection error/i.test(message || '');
            // Transient Hasura schema-cache miss during replica sync (#13426)
            const isTransientSchemaSync = isAuthenticatedHasuraSchemaSyncError(
              { message, extensions },
              operation.getContext()
            );
            // Story delete blocked by PromptResponse.story_id is expected (#10848).
            // The UI toasts it; do not report it (#16355).
            const isExpectedStoryDelete = isExpectedPromptResponseStoryDeleteError(message);
            if (!isTransientPg && !isTransientSchemaSync && !isExpectedStoryDelete) {
              captureMessage(`GraphQL error: ${message}`, {
                level: 'error',
                extra: {
                  operation: operation.operationName,
                  variables: operation.variables,
                  query: print(operation.query),
                  locations,
                  path,
                  extensions,
                }
              });
            }
          });
        }

        if (networkError) {
          if (isAuthenticatedHasuraSchemaSyncError(networkError, operation.getContext())) {
            console.warn(`[Hasura schema-sync] ${networkError.message} - Operation: ${operation.operationName}`);
            return;
          }
          const statusCode = (networkError as any)?.statusCode || (networkError as any)?.status;
          // Report 5xx server errors to Sentry - they indicate genuine backend/infra
          // problems worth tracking. 4xx (and other non-5xx) network errors are client-side
          // / expected-flow errors and should not be reported to Sentry.
          if (statusCode >= 500 && statusCode < 600) {
            console.error(`[Network error ${statusCode}]: ${networkError.message} - Operation: ${operation.operationName}`);
            captureException(networkError, {
              extra: {
                operation: operation.operationName,
                variables: operation.variables,
                statusCode,
              }
            });
          } else {
            console.error(`[Network error]: ${networkError.message}`);
          }
        }
      });

      // Get the required services
      const authInitializer = inject(AuthInitializerService);
      const authService = inject(AuthService);
      const webSocketService = inject(WebSocketService);

      // Get the WebSocket link from the WebSocketService
      const wsLink = webSocketService.getWebSocketLink();

      // Using the ability to split links, you can send data to each link
      // depending on what kind of operation is being sent
      const link = split(
        // split based on operation type
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        wsLink,
        httpLink,
      );

      // Create an AuthWaitLink that ensures auth is complete before operations
      const router = inject(Router);
      const authWaitLink = new AuthWaitLink(authInitializer, router, authService);

      // Add auth context
      const auth = createBearerTokenLink(authInitializer);

      return {
        link: new HasuraSchemaSyncLink().concat(errorLink.concat(retryLink.concat(authWaitLink.concat(auth.concat(link))))),
        cache: new InMemoryCache({
          // Configure cache size limits and garbage collection
          resultCaching: true,
          typePolicies: {
            Client: {
              fields: {
                // Don't cache large nested collections for settings pages
                activityTypes: {
                  merge: false, // Don't merge, replace completely
                },
                companyPermissions: {
                  merge: false,
                },
                webhooks: {
                  merge: false,
                },
                recommendations: {
                  merge: false,
                }
              }
            },
            Story: {
              fields: {
                allow_access_via_mcp: {
                  read(existing) { return existing ?? true; }
                }
              }
            },
            Board: {
              fields: {
                allow_access_via_mcp: {
                  read(existing) { return existing ?? true; }
                }
              }
            },
            Media: {
              fields: {
                allow_access_via_mcp: {
                  read(existing) { return existing ?? true; }
                }
              }
            },
            ActivityLog: {
              fields: {
                activityType: {
                  merge: true, // Merge activity type updates
                }
              }
            },
            ActivityType: {
              fields: {
                activityCategory: {
                  merge: true, // Merge activity category updates
                }
              }
            }
          }
        }),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          },
          query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          },
        }
      };
    }),

    // HTTP interceptors
    { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
    {
      provide: NGX_MONACO_EDITOR_CONFIG,
      useValue: monacoConfig
    },

    // Initialize services
    AuthInitializerService,
    { provide: AuthGuard, useClass: AuthGuard }, // Ensure AuthGuard is loaded
    WebSocketService,
    SafePipe,

    // Import modules that need to be provided at the application level
    importProvidersFrom(
      BrowserModule,
      UserIdleModule.forRoot({
        idle: environment.auth0SessionWarningSeconds,
        timeout: environment.auth0SessionTimeoutSeconds
      }),
      // Import ngx-bootstrap modules
      AccordionModule.forRoot(),
      AlertModule.forRoot(),
      ButtonsModule.forRoot(),
      CarouselModule.forRoot(),
      CollapseModule.forRoot(),
      BsDropdownModule.forRoot(),
      BsDatepickerModule.forRoot(),
      ModalModule.forRoot(),
      ProgressbarModule.forRoot(),
      TabsModule.forRoot(),
      TooltipModule.forRoot(),
      TypeaheadModule.forRoot(),
      RatingModule.forRoot(),
      PaginationModule.forRoot(),
      MonacoEditorModule.forRoot(monacoConfig)
    ),

    // Provide BsModalService
    BsModalService,

    // ErrorHandler MUST be AFTER importProvidersFrom(BrowserModule) to override the default
    {
      provide: ErrorHandler,
      useFactory: () => {
        const sentryHandler = createErrorHandler({
          showDialog: false,
          // Custom error extractor to handle cases where errors don't have standard structure
          // This prevents "Handled unknown error" messages in Sentry by extracting
          // meaningful information from non-standard error types
          extractor: (error: unknown, defaultExtractor: (error: unknown) => unknown): unknown => {
            // First try the default extractor
            const extracted = defaultExtractor(error);
            if (extracted !== null) {
              return extracted;
            }

            // Handle cases where default extraction failed
            // This captures the error information that would otherwise be lost
            if (error === undefined) {
              return new Error('Undefined error thrown');
            }

            if (error === null) {
              return new Error('Null error thrown');
            }

            // Handle objects that aren't Error instances but have useful properties
            if (typeof error === 'object') {
              const errorObj = error as Record<string, unknown>;

              // Try to extract message from common properties
              const message = errorObj['message'] || errorObj['error'] || errorObj['reason'] || errorObj['msg'];
              if (typeof message === 'string') {
                const err = new Error(message);
                // Preserve stack if available
                if (typeof errorObj['stack'] === 'string') {
                  err.stack = errorObj['stack'];
                }
                return err;
              }

              // If object has no message, stringify it for debugging
              try {
                return new Error(`Unknown error object: ${JSON.stringify(error)}`);
              } catch {
                return new Error(`Unknown non-serializable error object: ${Object.prototype.toString.call(error)}`);
              }
            }

            // Handle primitive types (numbers, booleans, etc.)
            return new Error(`Unknown error: ${String(error)}`);
          }
        });
        return {
          handleError(error: unknown): void {
            // Transient Hasura schema-cache miss during replica sync (#13426).
            // Subscriptions error the observable, which Angular's async pipe
            // forwards here; don't report it as an unhandled exception.
            if (isTransientHasuraSchemaSyncError(error)) {
              console.warn('[Hasura schema-sync] Ignoring transient GraphQL validation error:', error);
              return;
            }
            // Browser-aborted / unreachable requests surface as HTTP status 0
            // (#13431, #13432). nginx logs them as 499, never 5xx, so there is
            // nothing to act on server-side.
            if (isAbortedHttpRequestError(error)) {
              console.warn('[HTTP] Ignoring aborted request (status 0):', error);
              return;
            }
            // Sandboxed board embeds cannot update history. Firefox rejects
            // replaceState with SecurityError, which would otherwise surface
            // as an uncaught navigation promise (#15650).
            if (isInsecureHistoryError(error)) {
              console.warn('[history] Ignoring insecure history update in embed:', error);
              return;
            }
            // Survey wizard file PUT failed in the browser (offline / dropped
            // connection). The responder already asks the member to retry
            // (#16364 / SLAPFIVE-FRONTEND-A5D).
            if (isUploadNetworkError(error)) {
              console.warn('[upload] Ignoring expected XHR network failure:', error);
              return;
            }
            sentryHandler.handleError(error);
          }
        };
      }
    },
  ]
};
